import { Component, EventEmitter, OnInit, Output, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDividerModule } from '@angular/material/divider';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { FormsModule } from '@angular/forms';
import { OnboardingApiService } from '../../services/onboarding-api.service';

export interface DeviceItem {
  id: string;
  type: 'kiosk' | 'collector' | 'on-the-go';
  displayName: string;
  description: string;
  features: string[];
  quantity: number;
  simIncluded: boolean; // SIM card included in device
  simChecked: boolean; // User wants to activate SIM
}

export interface CartItem {
  deviceType: 'kiosk' | 'collector' | 'on-the-go';
  quantity: number;
  unitPrice: number; // cents
  isFirstDevice: boolean;
  simCount: number; // how many SIM cards
}

export interface DeviceCart {
  items: CartItem[];
  deviceSubtotal: number; // cents (devices only)
  setupFee: number; // cents ($100 per order)
  taxRate: number; // percentage (e.g., 0.08 for 8%)
  taxAmount: number; // cents (tax on devices)
  simFees: number; // cents (first month SIM charges: $10 per SIM)
  paymentPlan: 'now' | '12months'; // payment option
  totalToday: number; // cents (what user pays today)
  monthlyInstallment: number; // cents (device installment + SIM, if 12-month plan)
  total: number; // cents (total all payments over time)
}

@Component({
  selector: 'app-device-selection',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatCardModule,
    MatCheckboxModule,
    MatDividerModule,
    MatSnackBarModule,
    FormsModule,
  ],
  templateUrl: './device-selection.component.html',
  styleUrl: './device-selection.component.scss',
})
export class DeviceSelectionComponent implements OnInit {
  @Input() organizationId!: string;
  @Input() hideNewOrderForm = false;
  @Output() stepComplete = new EventEmitter<DeviceCart>();
  @Output() skipDevices = new EventEmitter<void>();

  existingOrders: any[] = [];
  loadingOrders = false;

  devices: DeviceItem[] = [
    {
      id: 'kiosk',
      type: 'kiosk',
      displayName: 'Lobby Kiosk',
      description: 'Stationary donation terminal for fundraising events, conferences, and outdoor venues. Donors interact directly with the screen for card donations.',
      features: [
        'Large touchscreen interface',
        'Card reader (contactless & chip)',
        'WiFi enabled',
        'Mains powered',
        'Professional appearance',
        'Event-ready setup',
        'Optional SIM for offline use',
      ],
      quantity: 0,
      simIncluded: true,
      simChecked: false, // unchecked by default (assumes WiFi available)
    },
    {
      id: 'collector',
      type: 'collector',
      displayName: 'Collector Device',
      description: 'In-office terminal operated by staff (like a store POS) to record cash/check donations and process card payments. Records donor info and amounts.',
      features: [
        'Desktop/counter setup',
        'Card reader (contactless & chip)',
        'Cash/check recording screen',
        'Mains powered',
        'Staff PIN protected',
        'Receipt capability',
        'Donor info capture',
      ],
      quantity: 0,
      simIncluded: true,
      simChecked: false,
    },
    {
      id: 'on-the-go',
      type: 'on-the-go',
      displayName: 'On-the-Go Device',
      description: 'Portable box device for outdoor campaigns, events, and field collections. Staff operates to process cash/check donations and card payments.',
      features: [
        'Portable box with touchscreen',
        'Card reader (contactless & chip)',
        'Cash/check handling optimized',
        'Optional SIM for connectivity',
        'Event-campaign ready',
        'Weatherproof design',
        'Durable battery',
      ],
      quantity: 0,
      simIncluded: true,
      simChecked: true, // SIM checked by default for on-the-go
    },
  ];

  cart: DeviceCart = {
    items: [],
    deviceSubtotal: 0,
    setupFee: 10000, // $100
    taxRate: 0.08, // 8% default (configurable by state)
    taxAmount: 0,
    simFees: 0, // $10 per SIM per month (first month charged upfront)
    paymentPlan: 'now', // default: pay all today
    totalToday: 0,
    monthlyInstallment: 0,
    total: 0,
  };

  hasDevices = false;
  paymentPlanOptions = ['now', '12months'] as const;

  constructor(
    private snackBar: MatSnackBar,
    private api: OnboardingApiService
  ) {}

  ngOnInit(): void {
    // Load existing device orders for this organization
    if (this.organizationId) {
      this.loadExistingOrders();
    }
  }

  loadExistingOrders(): void {
    this.loadingOrders = true;
    this.api.getDeviceOrders(this.organizationId).subscribe({
      next: (orders: any[]) => {
        this.existingOrders = orders;
        this.loadingOrders = false;
      },
      error: (err) => {
        console.warn('Failed to load device orders:', err);
        this.loadingOrders = false;
      },
    });
  }

  orderMore(): void {
    // Reset devices for new order
    this.devices.forEach(d => d.quantity = 0);
    this.cart = {
      items: [],
      deviceSubtotal: 0,
      setupFee: 10000,
      taxRate: 0.08,
      taxAmount: 0,
      simFees: 0,
      paymentPlan: 'now',
      totalToday: 0,
      monthlyInstallment: 0,
      total: 0,
    };
    this.existingOrders = [];
  }

  refreshOrders(): void {
    // Reload existing orders (called after payment completes)
    if (this.organizationId) {
      this.loadExistingOrders();
    }
  }

  addDevice(device: DeviceItem): void {
    if (device.quantity < 0) device.quantity = 0;
    device.quantity++;
    this.updateCart();
  }

  removeDevice(device: DeviceItem): void {
    if (device.quantity > 0) {
      device.quantity--;
      this.updateCart();
    }
  }

  updateCart(): void {
    this.cart.items = [];
    let simCount = 0;

    let isFirst = true;
    for (const device of this.devices) {
      if (device.quantity > 0) {
        // First device of any type: $399
        // Additional devices: $299
        const firstDevicePrice = 39900; // cents
        const additionalDevicePrice = 29900; // cents

        const firstDeviceCount = isFirst ? 1 : 0;
        const additionalCount = device.quantity - firstDeviceCount;

        // Count SIM cards if enabled for this device
        if (device.simIncluded && device.simChecked) {
          simCount += device.quantity;
        }

        if (firstDeviceCount > 0) {
          this.cart.items.push({
            deviceType: device.type,
            quantity: firstDeviceCount,
            unitPrice: firstDevicePrice,
            isFirstDevice: true,
            simCount: device.simIncluded && device.simChecked ? firstDeviceCount : 0,
          });
          isFirst = false;
        }

        if (additionalCount > 0) {
          this.cart.items.push({
            deviceType: device.type,
            quantity: additionalCount,
            unitPrice: additionalDevicePrice,
            isFirstDevice: false,
            simCount: device.simIncluded && device.simChecked ? additionalCount : 0,
          });
        }
      }
    }

    // Calculate device subtotal (devices only, no tax yet)
    this.cart.deviceSubtotal = this.cart.items.reduce(
      (sum, item) => sum + item.quantity * item.unitPrice,
      0
    );

    // Calculate sales tax on devices only (setup fee + SIM not taxed)
    this.cart.taxAmount = Math.round(this.cart.deviceSubtotal * this.cart.taxRate);

    // Calculate SIM fees: $10 per SIM (first month charged upfront)
    this.cart.simFees = simCount * 1000; // $10 = 1000 cents

    // Calculate payments based on plan
    if (this.cart.paymentPlan === 'now') {
      // Pay all today
      this.cart.totalToday = this.cart.deviceSubtotal + this.cart.taxAmount + this.cart.setupFee + this.cart.simFees;
      this.cart.monthlyInstallment = 0;
      this.cart.total = this.cart.totalToday;
    } else {
      // 12-month payment plan
      // Today: Setup fee + SIM first month + tax on devices
      this.cart.totalToday = this.cart.taxAmount + this.cart.setupFee + this.cart.simFees;
      // Monthly: Device installment (with tax included) + SIM recurring
      const deviceWithTax = this.cart.deviceSubtotal + this.cart.taxAmount;
      const deviceInstallment = Math.round(deviceWithTax / 12);
      this.cart.monthlyInstallment = deviceInstallment + this.cart.simFees; // includes SIM
      // Total over 12 months
      this.cart.total = this.cart.totalToday + this.cart.monthlyInstallment * 12;
    }

    this.hasDevices = this.cart.items.length > 0;
  }

  setPaymentPlan(plan: 'now' | '12months'): void {
    this.cart.paymentPlan = plan;
    this.updateCart();
  }

  getTotalDevices(): number {
    return this.devices.reduce((sum, d) => sum + d.quantity, 0);
  }

  formatPrice(cents: number): string {
    return (cents / 100).toFixed(2);
  }

  getMonthlyDevicePayment(): number {
    if (this.cart.paymentPlan !== '12months') return 0;
    return Math.round((this.cart.deviceSubtotal + this.cart.taxAmount) / 12);
  }

  proceedToPayment(): void {
    if (!this.hasDevices) {
      this.snackBar.open(
        'Please add at least one device to proceed.',
        'Close',
        { duration: 3000 }
      );
      return;
    }
    this.stepComplete.emit(this.cart);
  }

  skipDeviceSetup(): void {
    this.snackBar.open(
      'You can add devices later from your Organization Console.',
      'Close',
      { duration: 4000 }
    );
    this.skipDevices.emit();
  }

  continueOnboarding(): void {
    // Called from completion view "Continue to Billing" button
    // Emit empty cart to signal completion without payment
    this.stepComplete.emit({
      items: [],
      deviceSubtotal: 0,
      setupFee: 0,
      taxRate: 0,
      taxAmount: 0,
      simFees: 0,
      paymentPlan: 'now',
      totalToday: 0,
      monthlyInstallment: 0,
      total: 0,
    });
  }
}
